import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Report } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { ErrorBox } from './Watchlist';

// status 카테고리
// ⚠️ llm_extracted = 완료(done): cron_recompute(위키 단순화) 에선 리포트 terminal 이 llm_extracted —
//    위키 갱신은 cron 이 별도로 하고 그 상태는 위키 페이지의 "갱신 중/실패" 배지가 표시.
//    (옛 legacy_fanout 에선 중간상태였으나 컷오버 trigger flip 과 함께만 배포되므로 정합.)
const STATUS_CATEGORIES = {
  done: ['wiki_updated', 'llm_extracted'],
  processing: ['downloaded', 'text_extracted', 'text_embedded', 'embedded'],
  partial: ['text_failed', 'vision_failed'],
  failed: ['extract_failed', 'gave_up'],
} as const;

const STATUS_LABEL: Record<string, { label: string; cls: string; emoji: string }> = {
  downloaded: { label: '수집됨 · OCR 대기', cls: 'bg-slate-100 text-slate-700', emoji: '⬇️' },
  text_extracted: { label: 'text 추출됨', cls: 'bg-blue-100 text-blue-700', emoji: '📄' },
  text_embedded: { label: 'text embedded', cls: 'bg-indigo-100 text-indigo-700', emoji: '🔍' },
  embedded: { label: 'OCR 완료 · LLM 대기', cls: 'bg-purple-100 text-purple-700', emoji: '📄' },
  llm_extracted: { label: '완료', cls: 'bg-emerald-100 text-emerald-700', emoji: '✅' },
  wiki_updated: { label: '완료', cls: 'bg-emerald-100 text-emerald-700', emoji: '✅' },
  text_failed: { label: 'text 실패', cls: 'bg-orange-100 text-orange-700', emoji: '⚠️' },
  vision_failed: { label: 'OCR 실패', cls: 'bg-amber-100 text-amber-700', emoji: '⚠️' },
  extract_failed: { label: '분석 실패', cls: 'bg-rose-100 text-rose-700', emoji: '❌' },
  gave_up: { label: '실패', cls: 'bg-red-100 text-red-700', emoji: '❌' },
};

/** ISO 시각 from 지금까지 elapsed 를 "N분 째" 또는 "Nh Mm 째" 형식. */
function elapsedLabel(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso);
  const totalMin = Math.max(0, Math.floor(diffMs / 60_000));
  if (totalMin < 60) return `${totalMin}m 째`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m 째`;
}

function StatusBadge({
  status,
  batchName,
  batchSubmittedAt,
}: {
  status: string;
  batchName?: string | null;
  batchSubmittedAt?: string | null;
}) {
  // Phase C — batch 진행 중인 보고서는 별도 label + elapsed time.
  // batch_name 있으면 batch path 진행 중. downloaded + batch_name = OCR batch 대기.
  if (batchName) {
    const elapsed = batchSubmittedAt ? ` (${elapsedLabel(batchSubmittedAt)})` : '';
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
        title={`Gemini Batch API (50% 절감) 처리 중. submitted=${batchSubmittedAt ?? '?'}`}
      >
        🔄 batch 처리 중{elapsed}
      </span>
    );
  }
  const meta = STATUS_LABEL[status] ?? { label: status, cls: 'bg-slate-100 text-slate-700', emoji: '·' };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
      {meta.emoji} {meta.label}
    </span>
  );
}

/**
 * model name 을 family + version + variant short 로 압축.
 * `google-ai-studio/gemini-3.1-flash-lite` → `Gemini 3.1fl`
 * `gemini-3.1-flash` → `Gemini 3.1f`
 * `gemini-2.5-flash-lite-preview-09-2025` → `Gemini 2.5fl`
 * 매핑에 없는 variant 는 원본 유지 (`Gemini 3.1 unknown`).
 */
const VARIANT_SHORT: Record<string, string> = {
  'flash-lite': 'fl',
  flash: 'f',
  pro: 'p',
};

function shortModel(model: string | null | undefined): string {
  if (!model) return '—';
  const stripped = model.replace(/^[a-z0-9-]+\//, '');
  const match = stripped.match(/^gemini-([\d.]+)-(.+?)(?:-preview(?:-.*)?)?$/i);
  if (!match) return stripped;
  const version = match[1];
  const variant = match[2];
  if (!version || !variant) return stripped;
  const variantShort = VARIANT_SHORT[variant.toLowerCase()];
  return variantShort
    ? `Gemini ${version}${variantShort}`
    : `Gemini ${version} ${variant}`;
}

type FilterCategory = '' | 'done' | 'processing' | 'partial' | 'failed';

const FILTER_CATEGORY_LABEL: Record<Exclude<FilterCategory, ''>, string> = {
  done: '완료',
  processing: '처리중',
  partial: '부분 실패',
  failed: '영구 실패',
};

function categorize(status: string): 'done' | 'processing' | 'partial' | 'failed' | 'other' {
  for (const [cat, list] of Object.entries(STATUS_CATEGORIES)) {
    if ((list as readonly string[]).includes(status)) return cat as 'done' | 'processing' | 'partial' | 'failed';
  }
  return 'other';
}

export default function ReportsPage() {
  // category 별 client-side filter. server filter (status query) 를 쓰면 counter 가 fetched
  // items 기반이라 다른 category 가 0 으로 표시되는 버그. limit=500 으로 전체 fetch (backfill
  // 후 ~130 reports 까지 cover, 더 늘면 별도 count endpoint 필요).
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('');
  const { data, error, loading } = useAsync(
    () => api.reports(undefined, undefined, 500),
    [],
  );

  if (error) return <ErrorBox msg={error} />;
  if (loading) return <p className="text-slate-500">로딩 중…</p>;

  const items: Report[] = data?.items ?? [];
  const filteredItems = filterCategory
    ? items.filter((r) => categorize(r.status) === filterCategory)
    : items;

  // counter — 항상 전체 items 기반 (filter 와 무관, 정확)
  const counts = {
    total: items.length,
    done: items.filter((r) => categorize(r.status) === 'done').length,
    processing: items.filter((r) => categorize(r.status) === 'processing').length,
    partial: items.filter((r) => categorize(r.status) === 'partial').length,
    failed: items.filter((r) => categorize(r.status) === 'failed').length,
  };

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Reports 상태</h1>
        <p className="text-sm text-slate-500">{counts.total} 건</p>
      </div>

      {/* Counter cards — toggle: 같은 category 다시 누르면 전체 보기 */}
      <div className="mt-5 grid grid-cols-4 gap-3">
        <Counter label="완료" value={counts.done} color="emerald"
          active={filterCategory === 'done'}
          onClick={() => setFilterCategory(filterCategory === 'done' ? '' : 'done')} />
        <Counter label="처리중" value={counts.processing} color="indigo"
          active={filterCategory === 'processing'}
          onClick={() => setFilterCategory(filterCategory === 'processing' ? '' : 'processing')} />
        <Counter label="부분 실패" value={counts.partial} color="amber"
          active={filterCategory === 'partial'}
          onClick={() => setFilterCategory(filterCategory === 'partial' ? '' : 'partial')} />
        <Counter label="영구 실패" value={counts.failed} color="red"
          active={filterCategory === 'failed'}
          onClick={() => setFilterCategory(filterCategory === 'failed' ? '' : 'failed')} />
      </div>

      {/* Filter chip */}
      {filterCategory && (
        <div className="mt-4 flex items-center gap-2 text-sm">
          <span className="text-slate-500">filter:</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
            {FILTER_CATEGORY_LABEL[filterCategory]} · {filteredItems.length} 건
          </span>
          <button
            onClick={() => setFilterCategory('')}
            className="text-slate-500 hover:text-slate-900 underline"
          >
            전체 보기
          </button>
        </div>
      )}

      {/* Reports table */}
      <table className="mt-5 w-full text-sm">
        <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="py-2 pr-3">발행일</th>
            <th className="pr-3">종목</th>
            <th className="pr-3">브로커</th>
            <th className="pr-3">제목</th>
            <th className="pr-3 text-right">목표가</th>
            <th className="pr-3">Model</th>
            <th>상태</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {filteredItems.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50">
              <td className="py-2 pr-3 text-xs text-slate-500 whitespace-nowrap">
                {r.publishedAt ?? '—'}
              </td>
              <td className="pr-3 whitespace-nowrap">
                <Link
                  to={`/wikis/${r.symbol}`}
                  className="font-medium text-slate-900 hover:underline hover:text-indigo-600"
                >
                  {r.symbolName ?? r.symbol}
                </Link>
                <span className="ml-1.5 font-mono text-[10px] text-slate-400">{r.symbol}</span>
              </td>
              <td className="pr-3 text-xs">{r.broker ?? '—'}</td>
              <td className="pr-3 max-w-xs font-medium">
                <Link
                  to={`/reports/${r.id}/md`}
                  className="text-slate-900 hover:underline hover:text-indigo-600"
                  title="raw markdown 보기"
                >
                  <span className="truncate inline-block max-w-full align-bottom">{r.title}</span>
                </Link>
                <a
                  href={api.pdfUrl(r.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1.5 text-[10px] text-slate-400 hover:text-indigo-600"
                  title="원본 PDF 새 탭으로 열기"
                >
                  PDF↗
                </a>
              </td>
              <td className="pr-3 text-right font-mono text-xs">
                {r.targetPrice ? r.targetPrice.toLocaleString() : '—'}
              </td>
              <td className="pr-3 font-mono text-[11px] text-slate-500" title={r.model ?? undefined}>
                {shortModel(r.model)}
              </td>
              <td>
                <StatusBadge
                  status={r.status}
                  batchName={r.batchName}
                  batchSubmittedAt={r.batchSubmittedAt}
                />
              </td>
            </tr>
          ))}
          {filteredItems.length === 0 && (
            <tr>
              <td colSpan={7} className="py-6 text-center text-slate-500">
                {filterCategory ? '해당 카테고리의 reports 가 없습니다.' : '아직 reports 가 없습니다.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function Counter({
  label,
  value,
  color,
  onClick,
  active = false,
}: {
  label: string;
  value: number;
  color: string;
  onClick: () => void;
  active?: boolean;
}) {
  const colors: Record<string, string> = {
    emerald: 'border-emerald-200 bg-emerald-50',
    indigo: 'border-indigo-200 bg-indigo-50',
    amber: 'border-amber-200 bg-amber-50',
    red: 'border-red-200 bg-red-50',
  };
  const activeRing: Record<string, string> = {
    emerald: 'ring-2 ring-emerald-400',
    indigo: 'ring-2 ring-indigo-400',
    amber: 'ring-2 ring-amber-400',
    red: 'ring-2 ring-red-400',
  };
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border ${colors[color] ?? ''} ${active ? activeRing[color] ?? '' : ''} px-4 py-3 text-left transition hover:scale-105`}
    >
      <p className="text-xs text-slate-600">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </button>
  );
}
